/* Pillow Finder: explicit answer paths, live UK products, no inferred medical matches. */
(function () {
  'use strict';

  function resolve(config, answers) {
    let key = config.start;
    const path = [];
    for (const value of answers) {
      const node = config.nodes[key];
      const option = node && node.options.find((entry) => entry.value === value);
      if (!option) break;
      path.push({ node: key, value, label: option.label });
      key = option.next;
      if (key.startsWith('result:')) break;
    }
    return { key, path, node: config.nodes[key], result: config.results[key.replace(/^result:/, '')] };
  }

  function variantsFor(product, rule) {
    const variants = product.variants.filter((variant) => !/child|infant|junior/i.test(variant.title));
    return rule.variantMatch
      ? variants.filter((variant) => variant.title.toLowerCase().includes(rule.variantMatch.toLowerCase()))
      : variants;
  }

  function chooseVariant(variants, rule) {
    const available = variants.filter((variant) => variant.available);
    return (rule.defaultVariantMatch && available.find((variant) => variant.title.toLowerCase().includes(rule.defaultVariantMatch.toLowerCase()))) || available[0];
  }

  function normaliseProduct(data, handle) {
    if (!data || data.handle !== handle || !data.id || !data.available || !data.featured_image || !data.title) return null;
    const image = new URL(data.featured_image, 'https://www.kneepillow.co.uk');
    if (image.protocol !== 'https:') return null;
    const variants = (data.variants || []).filter((variant) => Number.isSafeInteger(variant.id) && Number.isFinite(variant.price));
    if (!variants.some((variant) => variant.available && !/child|infant|junior/i.test(variant.title))) return null;
    return { handle, title: data.title, image: image.href, variants, options: data.options || [] };
  }

  // Pure functions are exported only in Node, for the complete answer-path audit.
  if (typeof module === 'object' && module.exports) module.exports = { resolve, variantsFor, chooseVariant, normaliseProduct };
  if (typeof document === 'undefined') return;

  const escape = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char]));
  const money = (pence) => new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
  const track = (name, properties) => window.KPTheme?.track?.(name, properties || {});
  const arrow = '<svg aria-hidden="true" viewBox="0 0 24 24"><path d="m9 5 7 7-7 7"/></svg>';
  const icons = {
    neck: '<path d="M7 20c0-5 2-7 5-7s5 2 5 7M4 12l4-2m8 0 4 2"/><circle cx="12" cy="7" r="3"/>',
    legs: '<path d="M8 3v7c0 4 3 5 7 5s5 2 5 6M5 21h8"/>',
    seating: '<path d="M7 12V6a3 3 0 0 1 6 0v6M5 19v-4a3 3 0 0 1 3-3h8a3 3 0 0 1 3 3v4M3 22h18"/>',
    pregnancy: '<circle cx="12" cy="5" r="2"/><path d="M9 21v-6c0-4 1-6 4-6m0 0c5 0 6 8 0 8H9m0 0 3 4"/>',
    specialist: '<path d="M3 12h4l3-6 4 12 3-6h4"/>'
  };

  function init(wrapper) {
    if (wrapper.dataset.pillowFinderInitialized) return;
    wrapper.dataset.pillowFinderInitialized = 'true';
    const view = wrapper.querySelector('[data-finder-view]');
    const progress = wrapper.querySelector('[data-finder-progress]');
    const stepLabel = wrapper.querySelector('[data-finder-step]');
    const stepCount = wrapper.querySelector('[data-finder-count]');
    const back = wrapper.querySelector('[data-action="back"]');
    const restart = wrapper.querySelector('[data-action="restart"]');
    let config;
    try { config = JSON.parse(wrapper.querySelector('[data-product-data]').textContent); }
    catch (_) {
      view.innerHTML = '<h2>Let’s help you find a pillow</h2><p>The quiz could not load. Please refresh, or <a href="/pages/contact">contact us for help choosing</a>.</p>';
      return;
    }

    const cache = new Map();
    const cardProducts = new Map();
    let state = { answers: [], browse: false };
    let generation = 0;
    let adding = false;
    let disposed = false;
    let marketCheck;
    const historyKey = 'kpPillowFinder';
    const initialHistory = history.state?.[historyKey];
    if (initialHistory && Array.isArray(initialHistory.answers)) state = cleanState(initialHistory);

    function cleanState(value) {
      const answerValues = Array.isArray(value?.answers) ? value.answers.slice(0, 3).filter((answer) => typeof answer === 'string') : [];
      return { answers: resolve(config, answerValues).path.map((entry) => entry.value), browse: value?.browse === true };
    }

    function saveHistory(replace) {
      history[replace ? 'replaceState' : 'pushState']({ ...history.state, [historyKey]: { answers: [...state.answers], browse: state.browse } }, '');
    }

    function focusHeading() {
      const heading = view.querySelector('h2');
      heading?.focus({ preventScroll: true });
      // Account for the sticky header, including after device rotation.
      const header = document.querySelector('m-header');
      // The wrapper becomes fixed independently of m-header and reappears on upward scrolling.
      const stickyHeight = ['always', 'on_scroll_up'].includes(header?.dataset.sticky)
        ? header.querySelector('.m-header__wrapper')?.offsetHeight || 0 : 0;
      const headerBottom = Math.max(stickyHeight, header?.getBoundingClientRect().bottom || 0, 0);
      const top = wrapper.getBoundingClientRect().top;
      if (Math.abs(top - Math.min(headerBottom, 150) - 16) > 2) {
        window.scrollTo({ top: window.scrollY + top - Math.min(headerBottom, 150) - 16, behavior: 'instant' });
      }
    }

    async function requestProduct(handle) {
      if (cache.has(handle)) return cache.get(handle);
      const promise = (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        try {
          // The pillow range is UK-only. Never use Shopify.routes.root or a response's market URL.
          const response = await fetch('/products/' + encodeURIComponent(handle) + '.js', { signal: controller.signal, credentials: 'same-origin' });
          if (response.status === 404 || response.status === 410) return { product: null, missing: true };
          if (!response.ok) throw new Error('Product request failed');
          return { product: normaliseProduct(await response.json(), handle), missing: true };
        } catch (_) {
          cache.delete(handle); // A temporary failure can be retried; never substitute an invented product.
          return { product: null, temporary: true };
        } finally { clearTimeout(timer); }
      })();
      cache.set(handle, promise);
      return promise;
    }

    function checkCurrency() {
      // A direct UK URL can still have a basket left in a previous market.
      if (!marketCheck) marketCheck = (async () => {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), 8000);
        try {
          const response = await fetch('/cart.js', { credentials: 'same-origin', signal: controller.signal });
          if (!response.ok) return null;
          return (await response.json()).currency || null;
        } catch (_) { return null; }
        finally { clearTimeout(timer); }
      })();
      return marketCheck;
    }

    async function loadCandidates(rules) {
      const outcomes = new Array(rules.length);
      let cursor = 0;
      async function worker() {
        while (cursor < rules.length) {
          const index = cursor++;
          const rule = rules[index];
          const loaded = await requestProduct(rule.handle);
          const variants = loaded.product ? variantsFor(loaded.product, rule) : [];
          const selected = chooseVariant(variants, rule);
          outcomes[index] = { ...loaded, rule, variants, selected };
        }
      }
      await Promise.all(Array.from({ length: Math.min(4, rules.length) }, worker));
      return outcomes;
    }

    function updateProgress(context) {
      const complete = Boolean(context.result) || state.browse;
      const step = complete ? 3 : state.answers.length + 1;
      progress.setAttribute('aria-valuenow', String(step));
      progress.setAttribute('aria-valuetext', complete ? 'Your matches are ready' : 'Question ' + step);
      progress.style.setProperty('--pf-progress', (complete ? 100 : step / 3 * 100) + '%');
      stepLabel.textContent = complete ? (state.browse ? 'Explore the range' : 'Your matches') : ['Your need', 'The details', 'Your preference'][step - 1];
      stepCount.textContent = complete ? 'Ready to compare' : 'Question ' + step + ' of up to 3';
      back.hidden = state.answers.length === 0 && !state.browse;
      restart.hidden = state.answers.length === 0 && !state.browse;
    }

    function recap(context) {
      if (state.browse) return '';
      return '<div class="pf-recap" aria-label="Your answers. Select an answer to change it.">' + context.path.map((entry, index) =>
        '<button type="button" data-edit="' + index + '" aria-label="Change answer: ' + escape(entry.label) + '">' + escape(entry.label) + '<span aria-hidden="true"> ↗</span></button>'
      ).join('') + '</div>';
    }

    function productURL(handle, id) { return '/products/' + encodeURIComponent(handle) + '?variant=' + id; }

    function cardHTML(entry, index, browse, bestVerified) {
      const { product, rule, variants, selected } = entry;
      cardProducts.set(product.handle, entry);
      const href = productURL(product.handle, selected.id);
      const image = new URL(product.image); image.searchParams.set('width', '640');
      const optionNames = product.options.map((option) => typeof option === 'string' ? option : option.name).filter((name) => name && name !== 'Title').join(' / ');
      const optionTitle = optionNames || 'Option';
      return '<article class="pf-card' + (index === 0 && !browse && bestVerified ? ' pf-card--best' : '') + '" data-product-handle="' + escape(product.handle) + '">' +
        '<div class="pf-card-label">' + (browse ? 'Explore this type' : index === 0 && bestVerified ? 'Your closest match' : 'Also worth considering') + '</div>' +
        '<a class="pf-card-image" href="' + href + '" aria-label="View ' + escape(product.title) + '"><img src="' + escape(image.href) + '" width="320" height="240" loading="' + (index < 3 ? 'eager' : 'lazy') + '" alt="' + escape(product.title) + '"></a>' +
        '<div class="pf-card-info"><h3><a href="' + href + '">' + escape(product.title) + '</a></h3>' +
        '<p class="pf-card-price" data-price>' + money(selected.price) + '</p>' +
        '<p class="pf-card-reason">' + escape(rule.reason) + '</p></div>' +
        '<div class="pf-card-buy">' + (variants.length > 1
          ? '<label class="pf-variant"><span>' + escape(optionTitle) + '</span><select data-variant-select aria-label="Choose ' + escape(optionTitle.toLowerCase()) + ' for ' + escape(product.title) + '">' + variants.map((variant) => '<option value="' + variant.id + '"' + (variant.id === selected.id ? ' selected' : '') + (variant.available ? '' : ' disabled') + '>' + escape(variant.title) + ' — ' + money(variant.price) + (variant.available ? '' : ' · Sold out') + '</option>').join('') + '</select></label>'
          : '<p class="pf-single-option">' + (selected.title === 'Default Title' ? 'One standard option' : escape(selected.title)) + '</p>') +
        '<button class="pf-button pf-add" type="button" data-action="add" data-variant-id="' + selected.id + '">Add to basket</button>' +
        '<a class="pf-details" href="' + href + '">View product details ' + arrow + '</a>' +
        '<p class="pf-card-status" role="status" data-atc-status></p></div></article>';
    }

    async function render(moveFocus) {
      const token = ++generation;
      cardProducts.clear();
      const context = resolve(config, state.answers);
      const result = state.browse ? config.results.explore : context.result;
      updateProgress(context);
      view.removeAttribute('aria-busy');
      wrapper.classList.toggle('pf-panel--results', Boolean(result));
      if (!result) {
        const node = context.node || config.nodes.root;
        view.innerHTML = '<div class="pf-question-heading"><h2 tabindex="-1">' + escape(node.title) + '</h2><p>' + escape(node.help) + '</p></div>' +
          '<div class="pf-options' + (context.key === 'root' ? ' pf-options--root' : '') + '">' + node.options.map((option) =>
            '<button type="button" class="pf-option" data-answer="' + escape(option.value) + '">' +
            (context.key === 'root' ? '<span class="pf-option-icon"><svg aria-hidden="true" viewBox="0 0 24 24">' + icons[option.value] + '</svg></span>' : '') +
            '<span class="pf-option-copy"><strong>' + escape(option.label) + '</strong><span>' + escape(option.description) + '</span></span>' + arrow + '</button>'
          ).join('') + '</div>' + (state.answers.length === 0
            ? '<div class="pf-quiz-foot"><span>Adult support · No email needed</span><button class="pf-text-button" type="button" data-action="browse">Not sure? Explore the range ' + arrow + '</button></div>'
            : '<p class="pf-choice-hint">You can go back and change any answer.</p>');
        if (moveFocus) focusHeading();
        return;
      }

      view.innerHTML = '<div class="pf-results-heading"><h2 tabindex="-1">' + escape(result.title) + '</h2><p>' + escape(result.summary) + '</p></div>' + recap(context) +
        (result.note ? '<p class="pf-fit-note">' + escape(result.note) + '</p>' : '') +
        '<div data-results-grid class="pf-results-grid"><p class="pf-loading" role="status">Checking current prices and availability…</p></div>';
      if (moveFocus) focusHeading();
      const grid = view.querySelector('[data-results-grid]');
      if (!result.candidates.length) {
        grid.innerHTML = '<div class="pf-empty"><p>We can help explain product sizes and features once you know the shape you need.</p><a class="pf-button" href="/pages/contact">Get help with a product</a></div>';
        return;
      }
      view.setAttribute('aria-busy', 'true');
      const [loaded, currency] = await Promise.all([loadCandidates(result.candidates), checkCurrency()]);
      if (token !== generation || disposed) return;
      if (currency !== 'GBP') {
        const ukForm = wrapper.querySelector('[data-finder-uk-session]');
        if (currency && ukForm) grid.replaceChildren(ukForm.content.cloneNode(true));
        else grid.innerHTML = '<div class="pf-empty"><h3>We couldn’t check UK prices just now</h3><p>Your answers are saved here. Try again to check current prices.</p><button class="pf-button" type="button" data-action="retry">Check availability again</button></div>';
        view.removeAttribute('aria-busy');
        return;
      }
      const available = loaded.filter((entry) => entry.product && entry.selected).slice(0, state.browse ? 6 : 3);
      const temporary = loaded.some((entry) => entry.temporary);
      // If the preferred match could not be checked, don't label the next one the closest match.
      const bestVerified = !loaded.slice(0, loaded.findIndex((entry) => entry.product && entry.selected)).some((entry) => entry.temporary);
      if (!available.length) {
        grid.innerHTML = '<div class="pf-empty"><h3>' + (temporary ? 'We couldn’t check these products just now' : 'No matching option is available right now') + '</h3><p>' + (temporary ? 'Your answers are saved here. Try again to check live prices and stock.' : 'We have kept your choices. Change an answer or ask us about suitable options.') + '</p><button class="pf-button" type="button" data-action="retry">Check availability again</button><a href="/pages/contact">Ask us for help</a></div>';
      } else {
        grid.dataset.count = String(available.length);
        grid.innerHTML = available.map((entry, index) => cardHTML(entry, index, state.browse, bestVerified)).join('');
        if (temporary) grid.insertAdjacentHTML('afterend', '<p class="pf-partial" role="status">Some options could not be checked. <button class="pf-text-button" type="button" data-action="retry">Check again</button></p>');
        grid.querySelectorAll('img').forEach((image) => image.addEventListener('error', () => {
          image.hidden = true;
          image.parentElement.insertAdjacentHTML('beforeend', '<span class="pf-photo-error">Product photo unavailable</span>');
        }, { once: true }));
        track('finder_complete', { result_count: available.length, question_count: state.answers.length });
      }
      view.removeAttribute('aria-busy');
    }

    async function addToBasket(button) {
      if (adding || button.disabled) return;
      const card = button.closest('[data-product-handle]');
      const entry = cardProducts.get(card.dataset.productHandle);
      const variant = entry?.variants.find((item) => item.id === Number(button.dataset.variantId) && item.available);
      if (!variant) return;
      const status = card.querySelector('[data-atc-status]');
      adding = true;
      button.disabled = true;
      button.textContent = 'Adding…';
      const select = card.querySelector('select');
      if (select) select.disabled = true;
      status.textContent = '';
      track('cart_add_attempt', { source: 'pillow_finder', variant_id: variant.id, quantity: 1 });
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      let response;
      try {
        const sections = ['cart-drawer', 'cart-count'];
        response = await fetch('/cart/add.js', {
          method: 'POST', credentials: 'same-origin', signal: controller.signal,
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections, sections_url: '/pages/pillow-finder' })
        });
        const payload = await response.json();
        if (!response.ok || payload.status) {
          const error = new Error(payload.description || 'This option could not be added. Check its availability and try again.');
          error.confirmedFailure = true;
          throw error;
        }
        const added = payload.items?.[0];
        if (!added?.id) throw new Error('The basket response could not be confirmed.');
        button.textContent = 'Added to basket';
        status.innerHTML = '1 added. <a href="/cart">View basket</a>';
        if (typeof window.KPTheme?.added === 'function') window.KPTheme.added(added, { source: 'pillow_finder', quantity: 1 });
        else track('cart_add_success', { source: 'pillow_finder', variant_id: variant.id, quantity: 1, product_sku: added.sku, product_price: (added.price / 100).toFixed(2) });
        // Cart rendering is separate from the confirmed add: a drawer error must never prompt a duplicate add.
        try {
          if (payload.sections?.['cart-drawer'] && payload.sections?.['cart-count'] && window.MinimogEvents && window.MinimogTheme) {
            window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, { source: 'pillow_finder', cart: payload });
            if (window.MinimogSettings?.enable_cart_drawer) document.querySelector('m-cart-drawer')?.open(button);
          }
        } catch (_) { /* The confirmed basket link remains usable if a theme widget cannot refresh. */ }
      } catch (error) {
        button.disabled = false;
        button.textContent = 'Add to basket';
        if (error.confirmedFailure) status.textContent = error.message;
        else status.innerHTML = 'We couldn’t confirm the add. <a href="/cart">Check your basket</a> before trying again.';
        track('cart_add_failure', { source: 'pillow_finder', variant_id: variant.id, quantity: 1 });
      } finally {
        clearTimeout(timer);
        adding = false;
        if (select) select.disabled = false;
      }
    }

    wrapper.addEventListener('click', (event) => {
      const answer = event.target.closest('[data-answer]');
      const edit = event.target.closest('[data-edit]');
      const action = event.target.closest('[data-action]')?.dataset.action;
      if (answer) {
        const context = resolve(config, state.answers);
        if (!context.node?.options.some((option) => option.value === answer.dataset.answer)) return;
        state.answers.push(answer.dataset.answer);
        state.browse = false;
        saveHistory(false);
        track('finder_step', { step: state.answers.length });
        render(true);
      } else if (edit) {
        state = { answers: state.answers.slice(0, Number(edit.dataset.edit)), browse: false };
        saveHistory(false); render(true);
      } else if (action === 'back') {
        state = { answers: state.browse ? [] : state.answers.slice(0, -1), browse: false };
        saveHistory(false); render(true);
      } else if (action === 'restart') {
        state = { answers: [], browse: false };
        saveHistory(false); track('finder_restart'); render(true);
      } else if (action === 'browse') {
        state = { answers: [], browse: true };
        saveHistory(false); render(true);
      } else if (action === 'retry') {
        cache.clear(); marketCheck = null; render(false);
      } else if (action === 'add') addToBasket(event.target.closest('[data-action="add"]'));
      const link = event.target.closest('a[href^="/products/"]');
      if (link) track('finder_result_click', { destination: link.pathname });
    });

    wrapper.addEventListener('change', (event) => {
      if (!event.target.matches('[data-variant-select]')) return;
      const card = event.target.closest('[data-product-handle]');
      const entry = cardProducts.get(card.dataset.productHandle);
      const variant = entry?.variants.find((item) => item.id === Number(event.target.value) && item.available);
      if (!variant) return;
      card.querySelector('[data-price]').textContent = money(variant.price);
      const button = card.querySelector('[data-action="add"]');
      button.dataset.variantId = String(variant.id);
      button.disabled = false;
      button.textContent = 'Add to basket';
      card.querySelector('[data-atc-status]').textContent = '';
      card.querySelectorAll('a[href^="/products/"]').forEach((link) => link.setAttribute('href', productURL(entry.product.handle, variant.id)));
    });

    function onPopstate(event) {
      state = cleanState(event.state?.[historyKey]);
      render(true);
    }
    window.addEventListener('popstate', onPopstate);
    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(wrapper)) { disposed = true; generation++; window.removeEventListener('popstate', onPopstate); }
    });
    saveHistory(true);
    render(false);
  }

  function initAll(root) { (root || document).querySelectorAll('[data-quiz-wrapper]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
